/**
 * .tuMetrics {"tu1": 0.85, "tu2": 0.87, "tu3": 6, "notes": "Card CRUD, subtasks, steps/subtask progress stats, hierarchy", "tuVersion": "2.5"}
 * .help "Card queries: fetch by company/sprint/roadmap, map from DB; step and subtask stats for flow cards."
 */
import { supabase } from '../../supabase';
import { Card, CardType } from '../../../types';
import { isWorkCardType } from '../../../utils/cardTypeUtils';
import {
  mapCardFromDB,
  mapCardToDB,
  type CardInsert,
  type CardUpdate,
} from './card-mappers';

export { mapCardFromDB, mapCardToDB, mapCardUpdatesToDB } from './card-mappers';

// --- Supabase Interaction Functions ---

// ============================================
// PERFORMANCE OPTIMIZATION: Pagination Support
// ============================================
// Strategy: Support optional pagination for Cards page (50 items).
// Flow view intentionally loads ALL cards (no pagination) as it's someone's
// actual work board and they need to see everything.
// ============================================
export const fetchCards = async (
  companyId: string, 
  selectedTeamId: string | null,
  options?: { limit?: number; offset?: number }
) => {
  void selectedTeamId;
  let query = supabase.from('cards').select('*');

  // Always filter by company_id
  query = query.eq('company_id', companyId);

  query = query
    .order('position', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: false });

  // PERFORMANCE: Apply pagination if provided (for Cards page, not Flow view)
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, (options.offset + (options.limit || 0)) - 1);
  }

  const { data, error } = await query;

  if (error) throw error;
  
  // Map cards and fetch subtask stats for each card
  const cardsWithStats = await Promise.all(
    data.map(async (cardData) => {
      const card = mapCardFromDB(cardData);
      
      // Fetch step stats (checklist) and subtask stats (linked tasks) for flow cards
      if (isWorkCardType(card.type)) {
        try {
          const progress = await fetchCardProgressStats(card.id);
          if (progress) {
            if (progress.stepStats) card.stepStats = progress.stepStats;
            if (progress.subtaskStats) card.subtaskStats = progress.subtaskStats;
          }
        } catch (err) {
          console.warn(`Failed to fetch progress stats for card ${card.id}:`, err);
        }
      }
      
      return card;
    })
  );
  
  return cardsWithStats;
};

/**
 * Fetch all cards linked to a project (project_id = projectId).
 * Used by Project detail and Project card so the project view shows all project tasks
 * even when the global _allCards list is incomplete or filtered.
 */
export const fetchCardsByProjectId = async (
  companyId: string,
  projectId: string
): Promise<Card[]> => {
  const { data, error } = await supabase
    .from('cards')
    .select('*')
    .eq('company_id', companyId)
    .eq('project_id', projectId)
    .order('position', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map((row) => mapCardFromDB(row));
};

/** Planned task block for Dashboard Now calendar. Tasks with scheduled_date in range. */
export type PlannedTaskBlock = {
  cardId: string;
  title: string;
  assigneeId: string | null;
  projectKey: string;
  ticketNumber: number | null;
  scheduledDate: string; // YYYY-MM-DD
  scheduledStartTime: string | null; // HH:mm or HH:mm:ss
  scheduledDurationMinutes: number | null;
};

/**
 * Fetch planned task blocks for Dashboard Now calendar (scheduled_date in range).
 * Optional project filter. Uses idx_cards_scheduled_date_project.
 */
export const fetchPlannedTaskBlocks = async (
  companyId: string,
  projectId: string | null,
  startDate: string, // ISO date YYYY-MM-DD
  endDate: string
): Promise<PlannedTaskBlock[]> => {
  let query = supabase
    .from('cards')
    .select('id, title, assignee_id, scheduled_date, scheduled_start_time, scheduled_duration_minutes, ticket_number, projects(project_key)')
    .eq('company_id', companyId)
    .in('type', ['task', 'bug'])
    .not('scheduled_date', 'is', null)
    .gte('scheduled_date', startDate.slice(0, 10))
    .lte('scheduled_date', endDate.slice(0, 10));

  if (projectId) {
    query = query.eq('project_id', projectId);
  } else {
    query = query.not('project_id', 'is', null);
  }

  const { data, error } = await query.order('scheduled_date', { ascending: true }).order('scheduled_start_time', { ascending: true, nullsFirst: true });

  if (error) {
    console.error('[card-queries] fetchPlannedTaskBlocks error:', error);
    throw error;
  }

  // via unknown: PostgREST join typings return projects as an array-shaped any
  const rows = (data ?? []) as unknown as Array<{
    id: string;
    title: string | null;
    assignee_id: string | null;
    scheduled_date: string;
    scheduled_start_time: string | null;
    scheduled_duration_minutes: number | null;
    ticket_number: number | null;
    projects: { project_key: string } | null;
  }>;

  return rows.map((row) => ({
    cardId: row.id,
    title: row.title ?? '',
    assigneeId: row.assignee_id ?? null,
    projectKey: row.projects?.project_key ?? '',
    ticketNumber: row.ticket_number ?? null,
    scheduledDate: row.scheduled_date,
    scheduledStartTime: row.scheduled_start_time,
    scheduledDurationMinutes: row.scheduled_duration_minutes ?? null,
  }));
};

export const fetchCardById = async (cardId: string) => {
  const { data, error } = await supabase.from('cards').select('*').eq('id', cardId).single();

  if (error) throw error;
  return mapCardFromDB(data);
};

export const insertCard = async (cardData: CardInsert) => {
  const { data, error } = await supabase.from('cards').insert(cardData).select().single();

  if (error) throw error;
  return mapCardFromDB(data);
};

export const updateCardById = async (cardId: string, updates: CardUpdate) => {
  // December 2, 2025: Handle sprint_id updates via stored procedure (bypasses RLS)
  // This enables collaborative sprint planning where any company member can link cards to sprints
  if ('sprint_id' in updates) {
    const sprintId = updates.sprint_id;
    
    // Call stored procedure for sprint linking
    const { error: linkError } = await supabase.rpc('link_card_to_sprint', {
      p_card_id: cardId,
      p_sprint_id: sprintId
    });
    
    if (linkError) {
      console.error('❌ Error linking card to sprint:', linkError);
      throw linkError;
    }
    
    // Remove sprint_id from updates object so we don't try to update it again
    const { sprint_id: _linkedSprintId, ...remainingUpdates } = updates;
    void _linkedSprintId;
    
    // If there are other fields to update, do that via normal UPDATE
    if (Object.keys(remainingUpdates).length > 0) {
      const { data, error } = await supabase
        .from('cards')
        .update(remainingUpdates)
        .eq('id', cardId)
        .select()
        .single();
      
      if (error) {
        console.error('❌ Error updating card:', error);
        throw error;
      }
      return mapCardFromDB(data);
    } else {
      // Only sprint_id was being updated, fetch the updated card
      const { data, error } = await supabase
        .from('cards')
        .select('*')
        .eq('id', cardId)
        .single();
      
      if (error) {
        console.error('❌ Error fetching card after sprint link:', error);
        throw error;
      }
      return mapCardFromDB(data);
    }
  }
  
  // Normal update path (no sprint_id change)
  const { data, error } = await supabase.from('cards').update(updates).eq('id', cardId).select().single();

  if (error) {
    console.error('❌ Error updating card:', error);
    throw error;
  }
  return mapCardFromDB(data);
};

export const deleteCardById = async (cardId: string) => {
  const { error } = await supabase.from('cards').delete().eq('id', cardId);

  if (error) throw error;
  return true;
};

// Corrected to perform per-row updates for positions (RLS-friendly)
export const updateCardPositions = async (positions: { id: string; position: number | null }[]) => {
  const updates = positions.map((pos) =>
    supabase.from('cards').update({ position: pos.position }).eq('id', pos.id),
  );

  const results = await Promise.all(updates);

  for (const result of results) {
    if (result.error) {
      throw result.error;
    }
  }
  return true;
};

// December 4, 2025: Simplified - just update the type directly (no RPC needed)
// December 9, 2025: Finalize drafts when converting (remove is_draft flag)
export const rpcConvertCardType = async (cardId: string, newType: CardType, userId: string): Promise<Card> => {
  void userId;
  const { data, error } = await supabase
    .from('cards')
    .update({ 
      type: newType,
      // When converting idea → task, set status to 'todo' if not already set
      ...(newType === 'task' ? { status: 'todo' } : {}),
      // December 9, 2025: Finalize draft when converting (acts like Assign)
      is_draft: false
    })
    .eq('id', cardId)
    .select()
    .single();

  if (error) throw error;
  return mapCardFromDB(data);
};

// ============================================
// SUBTASK OPERATIONS
// ============================================

/**
 * Fetch all subtasks for a given parent card
 */
export const fetchCardSubtasks = async (parentCardId: string): Promise<Card[]> => {
  const { data, error } = await supabase.rpc('get_card_subtasks', {
    card_id: parentCardId,
  });

  if (error) {
    console.error('Error fetching card subtasks:', error);
    throw error;
  }

  return (data || []).map(mapCardFromDB);
};

/**
 * Fetch subtask statistics for a parent card (all children; legacy).
 */
export const fetchSubtaskStats = async (parentCardId: string) => {
  const { data, error } = await supabase.rpc('get_subtask_stats', {
    card_id: parentCardId,
  });

  if (error) {
    console.error('Error fetching subtask stats:', error);
    throw error;
  }

  return data;
};

/**
 * Fetch step stats (checklist items) and subtask stats (linked tasks) for flow cards.
 * Steps = children with no project_id; Subtasks = children with project_id.
 */
export const fetchCardProgressStats = async (parentCardId: string): Promise<{
  stepStats?: { total: number; todo: number; in_progress: number; done: number; under_review: number; blocked: number; completion_percentage: number };
  subtaskStats?: { total: number; todo: number; in_progress: number; done: number; under_review: number; blocked: number; completion_percentage: number };
} | null> => {
  const { data, error } = await supabase.rpc('get_card_progress_stats', {
    card_id: parentCardId,
  });

  if (error) {
    console.warn('Error fetching card progress stats:', error);
    return null;
  }

  if (!data) return null;
  const raw = data as { step_stats?: Record<string, number>; subtask_stats?: Record<string, number> };
  const result: {
    stepStats?: { total: number; todo: number; in_progress: number; done: number; under_review: number; blocked: number; completion_percentage: number };
    subtaskStats?: { total: number; todo: number; in_progress: number; done: number; under_review: number; blocked: number; completion_percentage: number };
  } = {};
  if (raw.step_stats) result.stepStats = raw.step_stats as typeof result.stepStats;
  if (raw.subtask_stats) result.subtaskStats = raw.subtask_stats as typeof result.subtaskStats;
  return result;
};

/**
 * Fetch card hierarchy (parent + all subtasks + stats)
 */
export const fetchCardHierarchy = async (cardId: string) => {
  const { data, error } = await supabase.rpc('get_card_hierarchy', {
    card_id: cardId,
  });

  if (error) {
    console.error('Error fetching card hierarchy:', error);
    throw error;
  }

  return data;
};

/**
 * Create a subtask for a parent card
 */
export const createSubtask = async (parentCardId: string, subtaskData: Partial<Card>): Promise<Card> => {
  const cardToInsert: CardInsert = {
    ...mapCardToDB(subtaskData as Card),
    parent_card_id: parentCardId,
    // Ensure subtask inherits company_id and other key fields from parent if not provided
    company_id: subtaskData.companyId,
    type: 'task', // Subtasks are always tasks
    status: subtaskData.status || 'todo',
  };

  const { data, error } = await supabase
    .from('cards')
    .insert(cardToInsert)
    .select()
    .single();

  if (error) {
    console.error('Error creating subtask:', error);
    throw error;
  }

  return mapCardFromDB(data);
};

/**
 * Remove parent-child relationship (convert subtask to standalone card)
 */
export const promoteSubtaskToCard = async (subtaskId: string): Promise<Card> => {
  const { data, error } = await supabase
    .from('cards')
    .update({ parent_card_id: null })
    .eq('id', subtaskId)
    .select()
    .single();

  if (error) {
    console.error('Error promoting subtask:', error);
    throw error;
  }

  return mapCardFromDB(data);
};